import type { CodeWorkspace } from "@intelligems/ig-types/functions/agentic-codegen/workspace";

/**
 * Domain binding for {@link parseWorkspace}: the domain's workspace schema
 * (structurally, anything zod-like with `safeParse`) plus its fixed entry
 * filename used when upgrading legacy single-file blobs.
 */
export interface WorkspaceCodec<Entry extends string> {
  schema: {
    safeParse: (input: unknown) => {
      success: boolean;
      data?: { entry: Entry; files: Record<string, string> };
    };
  };
  entryFilename: Entry;
}

/**
 * Single source of truth for turning a persisted custom-code source object
 * into a workspace and back. Every consumer (editor, bundler, test runner,
 * publish pipeline) routes through these helpers so the bytes stored at the
 * domain's S3 source key are interpreted identically everywhere.
 *
 * The persisted object is a JSON manifest. {@link parseWorkspace}
 * transparently upgrades a LEGACY single-file `.ts` blob (everything written
 * before the multi-file format) into a one-file workspace, so no data
 * migration is required and old drafts/versions keep working byte-for-byte.
 */
export function parseWorkspace<Entry extends string>(
  raw: string,
  codec: WorkspaceCodec<Entry>
): { entry: Entry; files: Record<string, string> } {
  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(raw);
  } catch {
    parsedJson = undefined;
  }

  if (parsedJson !== undefined) {
    const result = codec.schema.safeParse(parsedJson);
    if (result.success && result.data !== undefined) {
      return result.data;
    }
  }

  // Legacy single-file content: the entire blob is the entry module. (Raw
  // source is TypeScript, not JSON, so JSON.parse throws — or the parsed
  // value fails the workspace schema — and we land here.)
  return {
    entry: codec.entryFilename,
    files: { [codec.entryFilename]: raw },
  };
}

/** Serializes a workspace to the JSON manifest stored at the S3 source key. */
export function serializeWorkspace(workspace: CodeWorkspace): string {
  return JSON.stringify(workspace);
}

/** Returns the entry module's source (the file holding the default export). */
export function workspaceEntrySource(workspace: CodeWorkspace): string {
  return workspace.files[workspace.entry] ?? "";
}

/**
 * Returns every editable file in a workspace as `{ name, content }` pairs in a
 * stable display order: the entry module first, then the remaining files
 * alphabetically. Single source of truth for how a multi-file workspace is
 * laid out for reading (prompts, the read-only version-history view, ...).
 */
export function orderedWorkspaceFiles(
  workspace: CodeWorkspace
): { name: string; content: string }[] {
  const otherFilenames = Object.keys(workspace.files)
    .filter((filename) => filename !== workspace.entry)
    .sort();

  return [workspace.entry, ...otherFilenames].map((name) => ({
    name,
    content: workspace.files[name] ?? "",
  }));
}

/**
 * Renders every editable file in a workspace into a single annotated source
 * blob for prompts/diagnostics: the entry module first, then the remaining
 * files in alphabetical order, each prefixed with a `// ===== <filename> =====`
 * banner. This lets AI test generation (and any single-string consumer) see
 * the full multi-file workspace — not just the entry — while staying
 * byte-stable for a given workspace.
 */
export function flattenWorkspaceForPrompt(workspace: CodeWorkspace): string {
  return orderedWorkspaceFiles(workspace)
    .map(({ name, content }) => `// ===== ${name} =====\n${content}`)
    .join("\n\n");
}
